/**
 * Toolbar + chips rápidos do Cadastro de Suprimentos (redesign 2026-09-15).
 *
 * Busca com atalho "/", botão Filtros com badge (abre `FiltrosPanel`),
 * chips das dimensões ativas (removíveis), Exportar, segmentado
 * Lista | Por tipo e Colunas. Abaixo, os chips rápidos (Todos, Abaixo do
 * mínimo, Sem preço, Sem fornecedor, Excesso) com dot e contagem — o mesmo
 * estado que os KPIs ligam.
 *
 * No celular a busca ocupa a linha inteira e Exportar/Colunas saem daqui
 * (ficam no menu do cabeçalho); o segmentado continua, porque o
 * agrupamento também vale para os cards.
 */
import React from "react";
import { FiColumns, FiLayers, FiList, FiSearch, FiSliders, FiX } from "react-icons/fi";
import styled from "styled-components";

import ExportMenu from "../../../components/ExportMenu";
import { getToneTokens } from "../../../styles/shared";
import { Badge, Chip, IconBtn, SearchInput, SearchWrap, ToggleBtn, ViewToggle } from "../_shared/quadro/styles";
import { DIMENSOES, RAPIDOS, resumoChip } from "./filtros";

const Barra = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  @media (max-width: 768px) {
    ${SearchWrap} { flex: 1 1 100%; max-width: none; }
  }
`;

const Linha = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  > .resumo { margin-left: auto; font-size: 12px; color: var(--ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .resumo b { color: var(--ink-2); font-weight: 600; }
  > .resumo button { border: 0; background: none; padding: 0; color: var(--accent); font: inherit; font-size: 12px; cursor: pointer; }
  @media (max-width: 768px) { > .resumo { margin-left: 0; flex-basis: 100%; } }
`;

const CHIPS_RAPIDOS = ["abaixo", "sem_preco", "sem_fornecedor", "excesso"];

export function Toolbar({
  buscaRef, busca, onBusca, nFiltros, onAbrirFiltros, selecao, onRemoverDimensao,
  onExportar, exportarDesabilitado, vista, onVista, onColunas, mobile,
}) {
  return (
    <Barra>
      <SearchWrap>
        <FiSearch size={14} />
        <SearchInput
          ref={buscaRef}
          type="search"
          placeholder="Buscar código, nome, cor, fabricante, NCM…"
          value={busca}
          onChange={(e) => onBusca(e.target.value)}
          aria-label="Buscar suprimentos"
          style={{ height: mobile ? 44 : 38, borderRadius: 10 }}
        />
        {!mobile ? <kbd>/</kbd> : null}
      </SearchWrap>
      <button type="button" className="btn btn--secondary" onClick={onAbrirFiltros} style={{ height: mobile ? 44 : 38, borderRadius: 10 }}>
        <FiSliders size={14} /> Filtros {nFiltros ? <Badge>{nFiltros}</Badge> : null}
      </button>
      {DIMENSOES.filter((d) => selecao[d.key]).map((d) => (
        <Chip key={d.key} type="button" $on onClick={() => onRemoverDimensao(d.key)} title={`Remover filtro de ${d.rotulo.toLowerCase()}`}>
          {d.rotulo}: {resumoChip(selecao[d.key])} <span className="x"><FiX size={12} /></span>
        </Chip>
      ))}
      <span style={{ flex: 1 }} />
      {!mobile ? <ExportMenu onExport={onExportar} disabled={exportarDesabilitado} /> : null}
      <ViewToggle role="tablist" aria-label="Modo de visualização">
        <ToggleBtn type="button" role="tab" aria-selected={vista === "lista"} $on={vista === "lista"} onClick={() => onVista("lista")}>
          <FiList size={13} /> Lista
        </ToggleBtn>
        <ToggleBtn type="button" role="tab" aria-selected={vista === "tipo"} $on={vista === "tipo"} onClick={() => onVista("tipo")}>
          <FiLayers size={13} /> Por tipo
        </ToggleBtn>
      </ViewToggle>
      {!mobile ? (
        <IconBtn type="button" onClick={onColunas} title="Colunas da tabela" aria-label="Colunas da tabela" style={{ width: 38, height: 38, borderRadius: 10 }}>
          <FiColumns size={15} />
        </IconBtn>
      ) : null}
    </Barra>
  );
}

/** Chips rápidos: um ativo por vez; "Todos" = nenhum. */
export function ChipsRapidos({ rapido, onRapido, contagens, totalVisivel, resumoFiltro, onLimpar }) {
  return (
    <Linha>
      <Chip type="button" $on={!rapido} onClick={() => onRapido(null)}>
        <span className="dot" /> Todos <span className="n">{contagens.total ?? "—"}</span>
      </Chip>
      {CHIPS_RAPIDOS.map((id) => {
        const r = RAPIDOS.find((x) => x.id === id);
        return (
          <Chip
            key={id}
            type="button"
            $on={rapido === id}
            $cor={getToneTokens(null, r.tone).accent}
            onClick={() => onRapido(rapido === id ? null : id)}
            aria-pressed={rapido === id}
          >
            <span className="dot" /> {r.label} <span className="n">{contagens[id] ?? 0}</span>
          </Chip>
        );
      })}
      <span className="resumo">
        {resumoFiltro ? (
          <>
            Filtro: <b>{resumoFiltro}</b> · <button type="button" onClick={onLimpar}>limpar</button> · {totalVisivel} na tela
          </>
        ) : `${totalVisivel} na tela · ordenado por criticidade`}
      </span>
    </Linha>
  );
}
